import {
  LOCALES, useTranslation } from '../../../i18n';
import React from 'react';
import { C } from '../../../constants/theme';
import { CT } from '../../../constants/unifiedTheme';
import { TIME_SLOTS } from '../constants';
import { bookInputStyle, dropdownWrap, dropdownStyle } from '../styles';
import {
  dateToJalali, jalaliToDate, getInitials, serviceLabelOf, plannableServices,
  dayDateOf, dayIsoOf, slotInstant,
} from '../helpers';
import { bffMutation, bffQuery } from '../../../lib/bffClient';
import { jDaysInMonth } from '../../../lib/jalali';
import { FormField, JalaliDatePicker, NationalIdInput, Skeleton, SkeletonRegion, phone as phoneLib } from '@parsina/framework';
import { calendarSystem } from '@parsina/framework/ui/lib/brand';
import PhoneInput from '../../../components/common/PhoneInput';
import { toEnDigits } from '../../../lib/utils';
import {
  SlotModeSelector, FirstSlotPreview, useFirstAvailableSlot, useNextAvailableSlot, slotModeScope,
  useFirstPerformerSlot, useNextPerformerSlot,
  OptionsDisclosure, BOOKING_SLOT_MODES,
} from './FirstSlotPicker';
import { useSelectableSlots } from './useSelectableSlots';
import { useAutoFocus } from '../../../hooks/useAutoFocus';
import {
  useUntimed, UntimedRow, untimedSuggested, UNTIMED_DAY, UNTIMED_NOW,
} from './UntimedBooking';
import { useResponsive } from '../../../hooks/useResponsive';
import { useMedicalServiceTariffs } from '../../../hooks/useCashier';
import { useBookableServices } from '../../../hooks/usePractitionerServices';
import { useInsurers } from '../../../hooks/useInsurance';
import { useClinicRegion } from '../../../hooks/useClinicRegion';
import { useCountryDefaults } from '../../../hooks/useCountryDefaults';
import { insurerLabel, isBasic, registrationInsurers, SELF_PAY_INSURER_ID } from '../../../lib/insurance';
import InsuranceQuotePanel from '../../../components/common/InsuranceQuotePanel';
import NotifyPatientField from './NotifyPatientField';
import { useAppointmentNotificationSettings } from '../../../hooks/useAppointmentNotifications';
import { noticeBody, notifyBodyToSend, orderChannels } from '../../../lib/appointmentNotifications';
import {
  nidProblem, nidForWire, nidCountryFor, isRegisterable, phoneFlags, LINK_EXISTING_PATIENT,
  fileNumberProblem,
} from '../../../lib/patient';
import { usePhoneCandidates } from '../../../hooks/usePhoneCandidates';
import PhoneCandidateList from '../../../components/common/PhoneCandidateList';
import AdaptiveModal from '../../../components/common/AdaptiveModal';
import { STACK } from '../../../constants/density';
import { ConfirmDialog } from '@parsina/framework';
import { useBookableSlots } from '../../../hooks/useBookableSlots';
import { performerRow, resolveDuration, firstFree } from '../../Reception/bookingEngine';
import { usePrepaymentPolicy } from '../../../hooks/usePrepaymentPolicy';
import {
  BookingDepositBlock, depositFieldError, useBookingDeposit,
} from '../../../components/billing/BookingDepositBlock';
import { addDaysIso, clinicMinuteOfDay, clinicTodayIso } from '../../../lib/clinicZone';

// The clinic's next شماره پرونده — same suggestion endpoint AddPatient/CompleteFileModal
// use. A SUGGESTION only: nothing is reserved until the patient is actually saved.
const NEXT_FILE_NUMBER = `
  query NextFileNumber {
    nextFileNumber
  }
`;

const PATIENTS_SEARCH = `
  query SearchPatients($search: String) {
    patients(search: $search) { id name phone nationalId fileNumber }
  }
`;

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill={C.textMuted} width="13" height="13"
    style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
    <path d="M7 10l5 5 5-5z"/>
  </svg>
);

// Patient identification, two ways. «ثبت سریع» (a slot booked on a marker, with no
// patient record) was removed from this modal at K1's request (2026-10-06); rows booked
// that way before still exist and are completed through «تکمیل پرونده».
const PATIENT_MODES = (t) => ([
  { key: 'existing', label: t('calendar.tabs.existingPatient') },
  { key: 'new',      label: t('patients.actions.newPatient') },
]);

// `allowFirstAvailable` adds the «اولین نوبت» auto-pick modes on step 2. It is opt-in
// because the calendar opens this modal *on* a slot the user clicked — auto-picking a
// different time there would contradict the click (and break the optimistic insert, which
// is keyed by slot label). Screens with no slot context (reception) enable it.
export default function BookingModal({
  doctor, initialDate, initialSlotLabel, timeSlots, allowFirstAvailable = false, onClose, onSubmit: onSubmitProp,
  // ── «تأیید با زمان دیگر» (PATIENT_CLINIC_CARE_CONTEXT §9.1) ──────────────
  // A patient the caller has already decided on: the three patient modes
  // collapse to an identity line, because the question this modal is being
  // opened to answer is only WHEN. Its shape is the one `patientInfo` takes on
  // submit — { id, name, phone } — so nothing downstream has to know the modal
  // was preset.
  lockedPatient = null,
  // Epoch-ms instants (as strings) this practitioner already holds for a
  // patient REQUEST. Not a filter: a held slot stays selectable and the modal
  // warns instead (E8), because the desk may legitimately mean to take it —
  // and «silently unavailable» is how a request becomes invisible work.
  heldSlots = null,
  // What the primary button says when this is a confirmation rather than a
  // fresh booking. Persian lives in the caller's catalogue, never here.
  submitLabel = null,
  // «ارسال اعلان به بیمار». Off for «تأیید با زمان دیگر»: that patient asked from the
  // app, and the app is how they hear the answer.
  offerNotification = true,
  // `{ phone, candidates }` from the caller's `addPatient` refusal (PHONE_HAS_PATIENTS):
  // the number gained records the desk never answered for. A new object each time,
  // so a second refusal re-opens the question.
  phoneConflict = null,
  // ── A SESSION of a service a doctor ordered («اقدام‌های درمانی» › «نوبت بده») ──
  // `{ label, meta }`. The service is decided — the order names it — so it is shown
  // as a fact, not picked, and everything that belongs to a VISIT goes: visit type,
  // «خدمت نوبت», planned services, the insurance quote (the BFF prices the session
  // from its own declaration), «خارج از نوبت» and the notice (bookServiceSession
  // carries neither). `doctor` is then the PERFORMER, who may be a coworker rather
  // than a doctor — `doctor.shiftless` says they are held to no shift.
  // `service.notice` is a refusal the booking already met and must be read before
  // another try — «مرحلهٔ قبلی این پروسه هنوز انجام نشده است» for a step of a
  // treatment process that is not ready (ORDER_NOT_READY). Shown under the service.
  service = null,
  // A node the caller wants read BEFORE the form — «منشی هوشمند» uses it to say the
  // form was prefilled by AI (DESIGN_CONCEPT §13.3). Rendered above the time step.
  notice = null,
  // «پیش‌پرداخت» (PM-0657 §6.2): offer the deposit block. Only a fresh desk
  // booking asks for it (never an edit, a confirmation or a session); the answer
  // travels to `onSubmit` as `deposit: { amountRial, paymentMethod,
  // referenceNumber, requestId } | null` and the CALLER records it after the
  // booking, because a refused deposit must not cost the نوبت.
  offerDeposit = false,
  // `{ name, phone, nationalId, insurerId }` — a patient with no record yet, as
  // «منشی هوشمند» heard them. The patient step opens on «بیمار جدید» with what was said
  // in place; the desk checks it and fills the rest, and the submit is unchanged.
  presetNewPatient = null,
  // ── «ویرایش نوبت» (PM-0102) ─────────────────────────────────────────────────
  // `{ appointmentId, note }` — an appointment that already EXISTS. The patient
  // and the practitioner are facts of that row (`lockedPatient`, `doctor`), so
  // the form is the two things the desk may change on it: WHEN, and the note.
  // What the visit is FOR, its type and its insurance are not asked again — the
  // service has its own op («تغییر خدمت») and its own money rules. The time is
  // sent only when the desk touched it: `onSubmit({ dateTimeMs: null, … })`
  // means «the hour stays», so correcting a note never moves a نوبت that sits
  // off this grid.
  editing = null,
  // ── The slot ENGINE (PM-0600: the service-first dialog's features, in this form) ──
  // `{ performer: { type, id }, entries, doctorRow }` — the person being booked and
  // the services they perform (`bookingEngine.groupBookableServices`). Present, the
  // «خدمت نوبت» field lists THOSE services, each on its own length; the hours come
  // from `bookableSlots` (the shift's own anchors, the service's window, every live
  // booking); an hour the shift admits but the service does not is offered marked
  // and asked about; a QUEUE service takes a same-day turn instead of an hour.
  engine = null,
}) {
  const { t, formatMoney, formatDigits } = useTranslation();
  const serviceMode = !!service;
  const editMode = !!editing;
  const engineMode = !!engine && !serviceMode && !editMode;
  const notifyOn = offerNotification && !serviceMode;

  // «خدمت نوبت» on the engine: the person's visit first — the 90 % — else their first service.
  const engineEntries = engineMode ? engine.entries : [];
  const [entryId, setEntryId] = React.useState(null);
  const entry = engineMode
    ? (engineEntries.find((e) => e.catalogId === entryId)
      || engineEntries.find((e) => e.orderType === 'consultation')
      || engineEntries[0] || null)
    : null;
  const engineDuration = engineMode ? resolveDuration(entry, engine.performer, engine.doctorRow) : null;
  // The engine never guesses a length; a declaration written before lengths existed
  // reads as the 30 minutes it was written with (bookingEngine.entryFromOrder does the same).
  const engineMinutes = engineMode ? (engineDuration.minutes || 30) : null;
  const queue = engineMode && entry?.schedulingMode === 'QUEUE';
  const engineRow = engineMode ? performerRow(entry, engine.performer) : null;
  const [engineKey, setEngineKey] = React.useState(0);
  // An hour outside the service's window: asked about once (`outsideAsk`), then held.
  const [outsideAsk, setOutsideAsk] = React.useState(null);
  const [allowOutside, setAllowOutside] = React.useState(false);
  timeSlots = timeSlots || TIME_SLOTS;
  const slotMin = timeSlots[1] ? (timeSlots[1].h * 60 + timeSlots[1].m) - (timeSlots[0].h * 60 + timeSlots[0].m) : 30;

  const initJ  = dateToJalali(initialDate);
  const todayJ = dateToJalali(new Date());
  const years  = [todayJ.y, todayJ.y + 1];

  const [jYear,  setJYear]  = React.useState(initJ.y);
  const [jMonth, setJMonth] = React.useState(initJ.m);
  const [jDay,   setJDay]   = React.useState(initJ.d);

  const maxDay = jDaysInMonth(jYear, jMonth);
  React.useEffect(() => { if (jDay > maxDay) setJDay(maxDay); }, [jYear, jMonth, maxDay]);

  const selectedGDate = jalaliToDate(jYear, jMonth, jDay);

  // 'manual' = pick a time from the grid; 'firstOfDay'/'firstAny' = «اولین نوبت» auto-pick.
  // Auto-pick is the default when allowed and no slot was pre-clicked — booking the day's
  // first free slot is the common case. Opening the modal *on* a slot means one was chosen.
  const [slotMode, setSlotMode] = React.useState(
    allowFirstAvailable && !initialSlotLabel ? 'firstOfDay' : 'manual',
  );
  const isAuto = allowFirstAvailable && slotMode !== 'manual';

  // The booking opens on «اولین نوبت امروز» with everything else folded away, on
  // the desk exactly as on the phone (PM-0030): the mode row, the day and «خارج از
  // نوبت» sit behind one disclosure, and the optional extras (planned services,
  // notification, note) behind another. A modal that opens in «انتخاب دستی» (a
  // pre-clicked slot) opens its time options, since the desk came here to pick a time.
  const foldTime = allowFirstAvailable;
  const [timeOptionsOpen, setTimeOptionsOpen] = React.useState(slotMode !== 'firstOfDay');
  // An edit opens its extras: the note is one of the two things it is for.
  const [moreOpen, setMoreOpen] = React.useState(editMode);
  // «ویرایش نوبت»: has the desk asked for another time? Until it has, the grid
  // only SHOWS where the نوبت is and nothing about the time is sent.
  const [timeTouched, setTimeTouched] = React.useState(false);
  const touching = (set) => (v) => { setTimeTouched(true); set(v); };

  // «خارج از نوبت» — the same row in every tab, meaning what that tab can mean: a
  // DAY with no hour in «انتخاب دستی», and «همین الان» in the auto tabs, where the
  // patient is at the desk (PM-0056: it sits outside the folded time options, so it
  // is one tap away whichever tab is open). A service session has no such answer.
  const untimedMode = slotMode === 'manual' ? UNTIMED_DAY : UNTIMED_NOW;
  const untimed = useUntimed(untimedMode);
  // …and neither has an edit: «خارج از نوبت» is a way of BOOKING, not a time to move to.
  const offerUntimed = !serviceMode && !editMode;
  const isUntimed = offerUntimed && untimed.on;
  // A booking that came back "nothing free today" — the walk-in is now the suggestion.
  const [todayFull, setTodayFull] = React.useState(false);
  const { isMobile } = useResponsive();
  // Another doctor is another diary.
  React.useEffect(() => { setTodayFull(false); }, [doctor.id]);

  // The honest, selectable grid: presence-filtered and with booked slots removed. Skipped
  // (doctorId null) while auto-picking, since the grid isn't shown then.
  // Skipped (doctorId null) while auto-picking OR while «خارج از نوبت» is on: neither
  // shows the grid, and the day's booked list is only read to thin it out.
  const legacyGrid = useSelectableSlots({
    doctorId: isAuto || isUntimed || engineMode ? null : doctor.id,
    presences: doctor.presences,
    shiftless: !!doctor.shiftless,
    timeSlots,
    selectedDate: selectedGDate,
    slotMin,
    excludeAppointmentId: editing?.appointmentId || null,
  });

  // ── The engine's day (PM-0600) ─────────────────────────────────────────────
  // One read of the chosen day serves the grid AND «اولین نوبت امروز»: the first
  // free cell of that day is the auto-pick, so the two cannot disagree. Cells keep
  // the shape the grid already draws ({ label, h, m }) plus their instant: the
  // instant is the engine's, never rebuilt from the label.
  const engineDayIso = dayIsoOf(selectedGDate);
  const dayRead = useBookableSlots({
    enabled: engineMode && !queue && !isUntimed,
    serviceCatalogId: entry?.catalogId || null,
    performer: engineMode ? engine.performer : null,
    fromDate: engineDayIso,
    days: 1,
    durationMinutes: engineMinutes,
    refreshKey: engineKey,
  });
  const engineCells = React.useMemo(() => {
    if (!engineMode) return [];
    const nowMs = Date.now();
    const day = (dayRead.days || []).find((d) => d.date === engineDayIso);
    return (day?.slots || [])
      .filter((x) => Number(x.startMs) > nowMs && (x.free || x.reason === 'OUTSIDE_SERVICE_WINDOW'))
      .map((x) => {
        const ms = Number(x.startMs);
        const min = clinicMinuteOfDay(ms);
        const h = Math.floor(min / 60), m = min % 60;
        return { label: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`, h, m, ms, held: !x.free };
      });
  }, [engineMode, dayRead.days, engineDayIso]);
  const engineDayHasShift = engineMode && ((dayRead.days || []).find((d) => d.date === engineDayIso)?.slots || []).length > 0;
  const engineDayReady = engineMode && dayRead.ready && !dayRead.loading;
  // The grid's four answers, from whichever source this booking reads.
  const availSlots = engineMode ? engineCells : legacyGrid.slots;
  const slotsLoading = engineMode ? dayRead.loading : legacyGrid.loading;
  const noShift = engineMode ? false : legacyGrid.noShift;
  const absent = engineMode ? (engineDayReady && !dayRead.error && !engineDayHasShift) : legacyGrid.absent;
  const allBooked = engineMode ? (engineDayReady && engineDayHasShift && engineCells.length === 0) : legacyGrid.allBooked;

  const [selTime, setSelTime] = React.useState(initialSlotLabel || null);

  // Once an hour is CHOSEN, it is all the grid shows — fifty buttons around a decided
  // hour are noise on a form that should read in a glance (clinic CLAUDE.md §4.3).
  // Chosen: the hour the form was opened on («منشی هوشمند»'s proposal), or one the
  // desk picked from the grid. The grid opens again on «ساعت‌های دیگر», on another
  // day and on «انتخاب دستی» — each is the desk going looking — and whenever the
  // chosen hour is no longer free. An edit opens on its grid: moving the hour is
  // what it is for; it closes on the new hour like any other pick.
  const [gridClosed, setGridClosed] = React.useState(!!initialSlotLabel && !editMode);
  const slotsCollapsed = gridClosed && !!selTime && availSlots.some((s) => s.label === selTime);
  const lookingAgain = (set) => (v) => { setGridClosed(false); set(v); };

  // Keep the manual selection valid as the day (and therefore the free slots) change: keep
  // the current pick if it survived, else fall to the first free slot, else nothing.
  React.useEffect(() => {
    if (isAuto) return;
    // An edit nobody has re-timed marks the hour the نوبت HAS, when the grid can
    // show it, and otherwise marks nothing — never the first free slot, which
    // would be a move the desk did not ask for.
    if (editMode && !timeTouched) {
      setSelTime(availSlots.find(s => s.label === initialSlotLabel) ? initialSlotLabel : null);
      return;
    }
    if (availSlots.length === 0) { setSelTime(null); return; }
    // An hour outside the service's window is never chosen FOR the desk — it is asked about.
    const firstPlain = availSlots.find(s => !s.held);
    if (!availSlots.find(s => s.label === selTime)) setSelTime(firstPlain ? firstPlain.label : null);
  }, [availSlots, isAuto, timeTouched]);

  // DAY anchor is noon of the selected day ON THE CLINIC'S CLOCK (PM-0088), so the
  // service resolves the same calendar day whatever the browser's zone; ANY anchors
  // at "now".
  const fromDateTime = React.useMemo(() => {
    if (slotMode === 'firstOfDay') {
      return String(slotInstant(jalaliToDate(jYear, jMonth, jDay), 12, 0));
    }
    return String(Date.now());
  }, [slotMode, jYear, jMonth, jDay]);

  // A visit's «اولین نوبت» is the server's (the doctor's shift, at his visit length);
  // a service session's is searched here, on the performer, at the service's length
  // (FirstSlotPicker: useFirstPerformerSlot). Both hooks mount; one is enabled.
  const wantPreview = isAuto && !isUntimed && !!doctor.id && !engineMode;
  const doctorPreview = useFirstAvailableSlot(
    wantPreview && !serviceMode, doctor.id, fromDateTime, slotModeScope(slotMode),
  );
  const performerOpts = {
    providerId: doctor.id, presences: doctor.presences, shiftless: !!doctor.shiftless, timeSlots, slotMin,
  };
  const performerPreview = useFirstPerformerSlot({
    ...performerOpts, enabled: wantPreview && serviceMode, fromDateTime, scope: slotModeScope(slotMode),
  });
  const enginePreview = React.useMemo(() => {
    const hit = engineCells.find((c) => !c.held);
    return { slot: hit ? { dateTime: String(hit.ms) } : null, loading: dayRead.loading || !dayRead.ready, error: !!dayRead.error };
  }, [engineCells, dayRead.loading, dayRead.ready, dayRead.error]);
  const preview = engineMode ? enginePreview : serviceMode ? performerPreview : doctorPreview;
  const previewEmpty = isAuto && !isUntimed && !preview.loading && !preview.error && !preview.slot;

  // The date picker sits on today until the desk moves it — which is what «اولین نوبت
  // امروز» is named after, and what makes "the day is over" the right thing to say.
  const dayIsToday = jYear === todayJ.y && jMonth === todayJ.m && jDay === todayJ.d;

  // «خارج از نوبت» LEADS when the doctor has nothing free where this would land.
  const untimedLeads = offerUntimed && untimedSuggested({
    mode: untimedMode, slotMode, dayIsToday, previewEmpty,
    allBooked: slotMode === 'manual' && !slotsLoading && allBooked, todayFull,
  });

  // Only when the day itself came back empty: where the doctor's next real slot is.
  const wantNext = slotMode === 'firstOfDay' && previewEmpty && !isUntimed && !!doctor.id;
  const doctorNext = useNextAvailableSlot(wantNext && !serviceMode && !engineMode, doctor.id);
  const performerNext = useNextPerformerSlot(wantNext && serviceMode, performerOpts);
  const nextRead = useBookableSlots({
    enabled: engineMode && wantNext && !queue,
    serviceCatalogId: entry?.catalogId || null,
    performer: engineMode ? engine.performer : null,
    fromDate: addDaysIso(engineDayIso, 1),
    days: 14,
    durationMinutes: engineMinutes,
    refreshKey: engineKey,
  });
  const engineNext = React.useMemo(() => {
    const hit = firstFree(nextRead.days, Date.now());
    return { loading: nextRead.loading, error: !!nextRead.error, slot: hit ? { dateTime: String(hit.startMs) } : null };
  }, [nextRead.days, nextRead.loading, nextRead.error]);
  const nextAvailable = engineMode ? engineNext : serviceMode ? performerNext : doctorNext;

  // «رزرو همین نوبت» on the empty-day card moves the day to the slot's own day —
  // the day-scoped search there finds that same first free slot.
  const takeNextSlot = (slot) => {
    if (!slot?.dateTime) return;
    const j = dateToJalali(new Date(Number(slot.dateTime)));
    setJYear(j.y); setJMonth(j.m); setJDay(j.d);
  };

  // Visit type
  const [visitType, setVisitType] = React.useState('in_person');

  // ── Patient (step 2) ─────────────────────────────────────────────────────────
  const [mode, setMode] = React.useState(presetNewPatient ? 'new' : 'existing'); // 'existing' | 'new'
  // A preset patient is not a third mode: it REMOVES the question. The two
  // tabs, the search and the registration form all describe "which patient",
  // and here that is already answered.
  // An edit is locked the same way even on a «ثبت سریع» row, whose subject is a
  // label and not a patient id.
  const patientLocked = !!lockedPatient?.id || (editMode && !!lockedPatient);

  // Existing patient search
  const [searchQ,    setSearchQ]    = React.useState('');
  const [searchRes,  setSearchRes]  = React.useState([]);
  const [searching,  setSearching]  = React.useState(false);
  const [selPatient, setSelPatient] = React.useState(null);

  React.useEffect(() => {
    if (searchQ.length < 2) { setSearchRes([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        // A شماره پرونده is typed on a Persian keyboard and stored in ASCII.
        const data = await bffQuery(PATIENTS_SEARCH, { search: toEnDigits(searchQ.trim()) });
        setSearchRes(data?.patients || []);
      } finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ]);

  // New patient — same required fields as the ثبت بیمار جدید screen, so a patient registered
  // from a booking is a first-class record.
  const [newName,       setNewName]       = React.useState(presetNewPatient?.name || '');
  const [newPhone,      setNewPhone]      = React.useState('');
  const [newNid,        setNewNid]        = React.useState(presetNewPatient?.nationalId || '');
  // The issuing country — the clinic's unless the desk picked another (PM-0076).
  const [newNidCountry, setNewNidCountry] = React.useState(null);
  const [newInsurer,    setNewInsurer]    = React.useState(presetNewPatient?.insurerId || '');
  const [newFileNumber, setNewFileNumber] = React.useState('');
  const [fnBusy,        setFnBusy]        = React.useState(false);
  // The number the desk said they are registering someone NEW on, despite its records.
  const [sharedPhone, setSharedPhone] = React.useState('');
  // Set when an unlinked record was just added to this clinic from the phone lookup,
  // so the existing-patient view can say where the patient came from.
  const [linkedFrom,  setLinkedFrom]  = React.useState(null);

  const suggestFileNumber = async () => {
    setFnBusy(true);
    try {
      const data = await bffQuery(NEXT_FILE_NUMBER);
      if (data?.nextFileNumber) setNewFileNumber(data.nextFileNumber);
    } catch {
      // A suggestion we could not fetch is not a failure to register — the desk
      // types their own number, or leaves it blank.
    } finally {
      setFnBusy(false);
    }
  };

  // Where the clinic is. Abroad, «بیمار جدید» drops Iran: the mobile starts on
  // the clinic's country and the insurers are self-pay alone
  // (hooks/useClinicRegion.js).
  const region = useClinicRegion();
  // The kit's rule (PM-0076). Persian digits are read, not dropped — the old
  // `digitsOnly` deleted them and the code never reached the server (PM-0077).
  // The country: the desk's pick on the field's chip, else the clinic's — its
  // country's rule where the profile states one (PM-0218), else its region's.
  const countryDefaults = useCountryDefaults();
  const nidCountry = nidCountryFor(newNidCountry, region, countryDefaults.nationalIdCountry);
  const nidInvalid  = nidProblem(newNid, nidCountry) === 'invalid';
  // The country the new mobile starts on, and a dictated national number is read in.
  const phoneCountry = countryDefaults.phoneCountry || region.country || null;
  // Self-pay is the ONE insurer offered abroad, so it is the answer until the
  // desk gives another — derived rather than written into state, so a clinic
  // whose office line turns out to be Iranian goes back to asking.
  const insurerChoice = newInsurer || (region.iran ? '' : SELF_PAY_INSURER_ID);

  // The mobile «منشی هوشمند» heard, read in the clinic's country once it is known —
  // «۰۷۷۰…» at a clinic in Baghdad is an Iraqi number, not a broken Iranian one. Never
  // over what the desk has typed meanwhile.
  const presetPhone = presetNewPatient?.phone || '';
  React.useEffect(() => {
    if (!presetPhone) return;
    const e164 = phoneLib.normalizePhone(presetPhone, phoneCountry ? { country: phoneCountry } : undefined);
    if (e164) setNewPhone((typed) => typed || e164);
  }, [presetPhone, phoneCountry]);

  // ── The records already on the new patient's mobile ──────────────────────────
  // A mobile is a household, so a number that carries records is a question, not a
  // match: the person at the desk is one of them (book for that record) or someone new
  // on the same number. Asked inline, and the submit waits for the answer.
  const phoneLookup = usePhoneCandidates(mode === 'new' ? newPhone : '');
  const sharedPhoneOk = !!phoneLookup.number && sharedPhone === phoneLookup.number;
  const candidatesPending = phoneLookup.candidates.length > 0 && !sharedPhoneOk;

  function bookForRecord(patient, justLinked) {
    setSelPatient(patient);
    setLinkedFrom(justLinked ? patient : null);
    setSearchQ(''); setSearchRes([]);
    setMode('existing');
  }

  // Already the clinic's: nothing to create, just book for it.
  const pickLinkedCandidate = (c) =>
    bookForRecord(c.patient || { id: c.patientId, name: c.displayName, phone: phoneLookup.number }, false);

  // Another clinic's record: attach it here first, then book for it. Throws on
  // failure — the candidate row says so and stays actionable.
  const linkCandidate = async (c) => {
    const data = await bffMutation(LINK_EXISTING_PATIENT, { patientId: c.patientId });
    const linked = data?.linkExistingPatient;
    if (!linked?.id) throw new Error('linkExistingPatient returned no patient');
    bookForRecord(linked, true);
  };

  React.useEffect(() => {
    if (!phoneConflict) return;
    phoneLookup.seed(phoneConflict.phone, phoneConflict.candidates);
    setSharedPhone('');
  }, [phoneConflict]);

  const [notes,      setNotes]      = React.useState(editing?.note || '');
  const [submitting, setSubmitting] = React.useState(false);

  // «ارسال اعلان به بیمار». `null` means untouched, which follows the clinic's defaults
  // as they arrive; the first tick the desk changes makes the choice theirs for THIS
  // booking. A defaults read that fails is simply «nothing ticked».
  const { settings: notifyDefaults } = useAppointmentNotificationSettings({ enabled: notifyOn });
  const [notifyPick, setNotifyPick] = React.useState(null);
  const notifyChannels = notifyPick ?? orderChannels(notifyDefaults?.channels);
  // The desk's own words for THIS notice (PM-0556); null = the clinic's template. A
  // move («ویرایش نوبت») starts from the reschedule template, never the booking one.
  const [notifyText, setNotifyText] = React.useState(null);
  const notifyTemplate = noticeBody(notifyDefaults, editMode ? 'rescheduled' : 'booked');
  const notifyBody = notifyBodyToSend(notifyText, notifyTemplate);

  // What the desk PLANS to perform (target flow §1). Each pick is priced from the
  // clinic's declaration onto the proforma the booking raises; the visit fee itself
  // is always quoted from the doctor's contract and is not a pick here.
  const declared = useMedicalServiceTariffs();
  // One row per catalog service, priced for THIS doctor (helpers.plannableServices).
  // Keyed by catalogId below, so it must never carry the same catalog id twice —
  // per-doctor consultation declarations all share `consult-visit`.
  const plannable = React.useMemo(
    () => plannableServices(declared, doctor?.id || null),
    [declared, doctor?.id],
  );
  const [planned, setPlanned] = React.useState([]);   // [{ catalogId, quantity }]
  // A pick the new doctor has no price for (or a consultation) is not theirs to plan.
  React.useEffect(() => {
    setPlanned(prev => prev.filter(ps => plannable.some(svc => svc.catalogId === ps.catalogId)));
  }, [plannable]);

  // ── «خدمت نوبت» — the ONE service this appointment is for ──────────────────
  //
  // docs/CLINIC_RECEPTION_SERVICE_INVOICE_DESIGN.md §D2. Chosen here and nowhere
  // else: the proforma is priced from it, check-in re-prices the same row, and
  // the settle drawer shows it locked. Before this the visit named no service at
  // all and accounting guessed one from the clinic's consultation list.
  //
  // Pre-filled with the practitioner's own default, so the common booking takes
  // no extra keystroke. `serviceTouched` keeps a desk's deliberate choice from
  // being overwritten when the default arrives late or the doctor changes.
  const bookable = useBookableServices(isAuto || serviceMode || editMode || engineMode ? null : doctor?.id || null);
  const [serviceId, setServiceId] = React.useState(null);
  const [serviceTouched, setServiceTouched] = React.useState(false);
  React.useEffect(() => {
    if (!serviceTouched) setServiceId(bookable.defaultId);
  }, [bookable.defaultId, serviceTouched]);
  // The doctor changed: the previous person's service is not this one's.
  React.useEffect(() => { setServiceTouched(false); }, [doctor?.id]);
  const chosenService = bookable.services.find(sv => sv.declarationId === serviceId) || null;
  // What the desk decided about insurance for THIS visit: which بیمهٔ تکمیلی to
  // skip, and which معرفی‌نامه each should pay under. Never a change to the
  // patient's policies — those are edited on the patient, not on a booking.
  const [insuranceChoice, setInsuranceChoice] = React.useState(null);
  // ── «پیش‌پرداخت» (PM-0657 §6.2) — prefilled from the booked service's tariff ──
  const { policy: prepaymentPolicy } = usePrepaymentPolicy();
  const deposit = useBookingDeposit({
    policy: prepaymentPolicy,
    priceRial: (engineMode ? engineRow?.unitPrice : chosenService?.unitPrice) ?? null,
    applies: offerDeposit && !editMode && !serviceMode,
  });
  const [depositError, setDepositError] = React.useState('');
  const plannedQuote = planned.reduce((sum, ps) => {
    const d = plannable.find(svc => svc.catalogId === ps.catalogId);
    return sum + (d?.unitPrice || 0) * (ps.quantity || 1);
  }, 0);
  const togglePlanned = (catalogId) =>
    setPlanned(prev => prev.some(ps => ps.catalogId === catalogId)
      ? prev.filter(ps => ps.catalogId !== catalogId)
      : [...prev, { catalogId, quantity: 1 }]);

  function switchMode(m) {
    setMode(m); setSelPatient(null); setLinkedFrom(null); setSearchQ(''); setSearchRes([]);
  }

  const patientReady =
    patientLocked      ? true
    : mode === 'existing' ? !!selPatient
    // نام + a mobile the desk can call back on — this modal has no «ندارد»
    // escape. کد ملی and بیمه are optional (PM-0125), but a کد ملی that is
    // entered must be valid. A number that carries records waits for the desk's pick.
    : isRegisterable({
      name: newName, nid: newNid, nidCountry, insurer: insurerChoice, phone: newPhone, noPhone: false,
    })
      // A شماره پرونده that is typed has to be one (PM-0128) — said under the field.
      && !fileNumberProblem(newFileNumber)
      && !candidatesPending;

  // Step 1 is bookable when there is a concrete slot: a manual pick that survived
  // filtering, or a non-empty auto-pick preview. «خارج از نوبت» is bookable with
  // neither — having no hour is the whole point, so there is nothing left to wait for.
  // A session's auto-pick is resolved HERE, so it is ready only once the search
  // has a slot; a visit's is resolved by the server on submit.
  const timeReady = isUntimed || queue ? true : (isAuto ? (serviceMode || engineMode ? !!preview.slot : !previewEmpty) : !!selTime);
  // An edit that leaves the time alone has nothing to wait for; one that asked for
  // another day waits for an hour on it.
  const canSubmit = editMode
    ? (editing.needsTime ? timeTouched && !!selTime : (!timeTouched || !!selTime))
    : patientReady && timeReady;

  async function submit() {
    if (!canSubmit || submitting) return;
    // The amount a «الزامی» policy needs, or the reference a non-cash deposit
    // needs (PM-0702) — said under the deposit's own field.
    const depositError = depositFieldError(t, deposit);
    if (depositError) { setDepositError(depositError); return; }
    // Every booking answer carries the desk's deposit decision; an edit never does.
    const onSubmit = (args) => onSubmitProp({ ...args, deposit: deposit.wire });
    setSubmitting(true);
    try {
      // The modal hands the caller ONE subject, patientInfo. The caller registers the
      // patient for the `new` case.
      const subject =
        patientLocked
          ? { patientInfo: { id: lockedPatient.id, name: lockedPatient.name || '', phone: lockedPatient.phone || '' } }
        : mode === 'existing'
          ? { patientInfo: { id: selPatient.id, name: selPatient.name, phone: selPatient.phone || '' } }
          : { patientInfo: {
              id: null,
              name:       newName.trim(),
              phone:      newPhone,
              nationalId: nidForWire(newNid, nidCountry),
              ...phoneFlags({ noPhone: false, allowSharedPhone: sharedPhoneOk }),
              // The catalog id, not a display label: `patients.insurance` is
              // DERIVED from the policy now, so the insurer travels as an id and
              // the caller writes it as a policy against the new patient.
              insurerId:  insurerChoice,
              // Empty is a valid answer — same as ثبت بیمار جدید, nothing is reserved
              // until this save; ASCII whatever keyboard typed it.
              fileNumber: toEnDigits(newFileNumber).trim() || null
            } };

      const services = planned.map(({ catalogId, quantity }) => ({ catalogId, quantity }));
      // The service travels WITH the booking so the appointment stores it; the
      // BFF resolves one itself when this is null (a practitioner with nothing
      // declared, or an «اولین نوبت» booking whose doctor is not known yet).
      // On the engine it is the row that prices THIS performer, and the catalogue
      // id rides with it so the booking is held to that service's window.
      const serviceDeclarationId = engineMode ? (engineRow?.declarationId || null) : (serviceId || null);
      const engineExtras = engineMode ? { serviceCatalogId: entry?.catalogId || null } : {};
      // This visit's own insurance decisions travel with the booking, so the
      // proforma is priced under them. They land on the invoice's legs and never
      // on the patient's policies.
      const insurance = mode === 'existing' ? insuranceChoice : null;
      const notify = notifyOn ? notifyChannels : [];
      // «ویرایش نوبت»: the instant only when the desk chose one, and none of the
      // booking's other answers — they were given when the نوبت was booked.
      if (editMode) {
        const picked = timeTouched ? availSlots.find(s => s.label === selTime) : null;
        await onSubmit({
          ...subject,
          dateTimeMs: picked ? slotInstant(jalaliToDate(jYear, jMonth, jDay), picked.h, picked.m) : null,
          slotLabel: picked ? selTime : null, notes, notifyChannels: notify, notifyBody: notify.length ? notifyBody : null,
        });
        return;
      }
      // A QUEUE service holds no hour: a turn for today, ordered at the desk.
      if (engineMode && queue) {
        await onSubmit({
          ...subject, dateTimeMs: slotInstant(dayDateOf(clinicTodayIso()), 12, 0), slotLabel: null, notes,
          type: visitType, services, serviceDeclarationId, ...engineExtras,
          insuranceChoice: insurance, notifyChannels: notify, notifyBody: notify.length ? notifyBody : null,
          untimed: UNTIMED_DAY,
        });
        return;
      }
      // «خارج از نوبت»: no hour to send and no slot to search for. The day still
      // travels for UNTIMED_DAY — the service reads it for its DAY and ignores the
      // hour — and is ignored outright for «همین الان», which is today by definition.
      // `firstAvailable` is deliberately absent: the BFF refuses the two together,
      // because one searches for a free hour and this is the booking that has none.
      if (isUntimed) {
        // Noon of the picked day on the clinic's clock: the service reads its DAY.
        const dayMs = slotInstant(jalaliToDate(jYear, jMonth, jDay), 12, 0);
        await onSubmit({
          ...subject,
          dateTimeMs: untimedMode === UNTIMED_NOW ? Date.now() : dayMs,
          slotLabel: null, notes, type: visitType, services, serviceDeclarationId, ...engineExtras,
          insuranceChoice: insurance, notifyChannels: notify, notifyBody: notify.length ? notifyBody : null,
          untimed: untimedMode,
        });
        return;
      }

      if (isAuto) {
        // bookServiceSession has no `firstAvailable`: the session takes the slot
        // the search found, and the server re-checks it for a collision. The
        // engine's first free hour is booked the same way — the instant it named.
        if (serviceMode || engineMode) {
          if (!preview.slot?.dateTime) return;
          await onSubmit({
            ...subject, dateTimeMs: Number(preview.slot.dateTime), slotLabel: null, notes,
            type: visitType, services, serviceDeclarationId, ...engineExtras,
            insuranceChoice: insurance, notifyChannels: notify, notifyBody: notify.length ? notifyBody : null,
          });
          return;
        }
        await onSubmit({
          ...subject, dateTimeMs: Number(fromDateTime), slotLabel: null,
          notes, type: visitType, firstAvailable: slotModeScope(slotMode), services,
        serviceDeclarationId,
          insuranceChoice: insurance, notifyChannels: notify, notifyBody: notify.length ? notifyBody : null,
        });
        return;
      }

      const slot = availSlots.find(s => s.label === selTime) || availSlots[0];
      await onSubmit({
        ...subject, dateTimeMs: slot.ms != null ? slot.ms : slotInstant(jalaliToDate(jYear, jMonth, jDay), slot.h, slot.m),
        slotLabel: selTime, notes,
        type: visitType, services, serviceDeclarationId, ...engineExtras,
        ...(engineMode && slot.held && allowOutside ? { allowOutsideServiceWindow: true } : {}),
        insuranceChoice: insurance, notifyChannels: notify, notifyBody: notify.length ? notifyBody : null,
      });
    } catch (err) {
      // Today just ran out under the desk: the patient standing there is now a
      // walk-in. The caller still owns the error and how it is said.
      const code = err?.extensions?.code;
      if ((code === 'NO_SLOT_AVAILABLE' || code === 'SLOT_TAKEN' || code === 'APPOINTMENT_CONFLICT') && dayIsToday) {
        setTodayFull(true);
      }
      // The engine answered before somebody else booked: read the day again.
      if (engineMode) setEngineKey((k) => k + 1);
      throw err;
    } finally { setSubmitting(false); }
  }

  const days = Array.from({ length: maxDay }, (_, i) => i + 1);

  // The instant the desk currently has selected, derived the same way `submit`
  // derives it — a second derivation would eventually warn about a moment other
  // than the one being booked. Null under auto-pick: the server chooses there,
  // and it already refuses a slot a request is holding.
  const selectedMs = React.useMemo(() => {
    if (isAuto || isUntimed || !selTime) return null;
    const slot = availSlots.find((x) => x.label === selTime);
    if (!slot) return null;
    if (slot.ms != null) return slot.ms;
    return slotInstant(jalaliToDate(jYear, jMonth, jDay), slot.h, slot.m);
  }, [isAuto, selTime, availSlots, jYear, jMonth, jDay]);

  // E8: the slot is already held by a patient's request. The request holds it —
  // the scheduling service's overlap check counts `requested` rows — so booking
  // over it would be refused anyway; saying so here turns a refusal into a
  // decision, with the two ways out named.
  const heldByRequest = !!(heldSlots && selectedMs && heldSlots.has(String(selectedMs)));

  // One entry as the select names it: the service and THIS performer's price.
  const engineEntryLabel = (e) => {
    const row = performerRow(e, engine?.performer);
    return serviceLabelOf(row ? { ...row, description: e.name } : { description: e.name }, t, formatMoney);
  };

  const VISIT_TYPES = [
    { key: 'in_person', label: t('reception.filters.inPerson'),     icon: '🏥' },
    { key: 'online',    label: t('calendar.type.remote'),  icon: '💻' },
  ];

  // Both anatomies need these keyframes — `modalIn` for the desktop card and
  // `spin` for the submit button's spinner — so they travel with the modal
  // rather than with the card that used to own them.
  const keyframes = (
    <style>{`
      @keyframes modalIn { from { opacity:0; transform:scale(0.95) translateY(14px) } to { opacity:1; transform:scale(1) translateY(0) } }
      @keyframes spin     { to   { transform:rotate(360deg) } }
    `}</style>
  );

  // Who is being booked, and how. The desk reads it in the header; the phone
  // gets it at the top of the sheet, whose header is one line of title.
  const chips = (
    <>
      {/* Doctor chip + visit type */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C.bg, borderRadius: 20, padding: '5px 12px 5px 6px', minWidth: 0 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: doctor.color, color: 'white', fontSize: '0.6875rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {getInitials(doctor.name)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary, lineHeight: 1.2 }}>{doctor.name}</div>
            {engineMode && !queue ? (
              <div data-testid="booking-length" style={{ fontSize: '0.625rem', color: C.textMuted }}>
                {t(engineDuration.source === 'DOCTOR' ? 'reception.booking.lengthFromDoctor' : 'reception.booking.lengthFromService',
                  { minutes: formatDigits(engineMinutes) })}
              </div>
            ) : doctor.specialty && <div style={{ fontSize: '0.625rem', color: C.textMuted }}>{doctor.specialty}</div>}
          </div>
        </div>

        {serviceMode || editMode ? null : (
          <div style={{ display: 'flex', border: `1.5px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
            {VISIT_TYPES.map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => setVisitType(key)}
                style={{
                  padding: '6px 12px', border: 'none', cursor: 'pointer',
                  fontFamily: 'Meshkat, sans-serif', fontSize: '0.75rem', fontWeight: 700,
                  background: visitType === key ? C.navy : 'white',
                  color: visitType === key ? 'white' : C.textSecondary,
                  display: 'flex', alignItems: 'center', gap: 4,
                  transition: 'background 0.15s, color 0.15s'
                }}>
                <span style={{ fontSize: '0.8125rem' }}>{icon}</span>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );

  // The booking's optional extras, folded behind «گزینه‌های بیشتر» (the clinic's
  // notification defaults still apply while folded).
  const newPatientExtras = !patientLocked && mode === 'new';
  const extrasPlanned = (
    <>
      {/* Planned services — what the proforma will quote beside the visit fee. Only
          shown when the clinic has declared priced services; chips, not a select,
          because the desk picks one or two, fast. */}
      {!serviceMode && !editMode && plannable.length > 0 && (
        <FormField label={t('calendar.booking.plannedServices')}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {plannable.map(svc => {
              const on = planned.some(ps => ps.catalogId === svc.catalogId);
              return (
                <button
                  key={svc.catalogId}
                  type="button"
                  onClick={() => togglePlanned(svc.catalogId)}
                  title={t('calendar.booking.servicePrice', { amount: formatMoney(svc.unitPrice, { currency: svc?.currency }) })}
                  style={{
                    padding: '5px 10px', borderRadius: 20, fontSize: '0.75rem', fontFamily: 'inherit', cursor: 'pointer',
                    border: `1px solid ${on ? C.navy : C.border}`, background: on ? C.navy : '#fff', color: on ? '#fff' : C.textSecondary,
                  }}
                >
                  {svc.description}
                </button>
              );
            })}
          </div>
          {plannedQuote > 0 && (
            <div style={{ fontSize: '0.71875rem', color: C.textMuted, marginTop: 6 }}>
              {t('calendar.booking.plannedTotal', { amount: formatMoney(plannedQuote) })}
            </div>
          )}
        </FormField>
      )}

    </>
  );
  const extrasTail = (
    <>
      {/* «ارسال اعلان به بیمار» — ticked from «تنظیمات › اعلان‌ها», changeable here
          for this booking only. */}
      {notifyOn ? (
        <NotifyPatientField
          value={notifyChannels}
          onChange={setNotifyPick}
          template={notifyTemplate}
          text={notifyText}
          onTextChange={setNotifyText}
        />
      ) : null}

      {/* Notes — last field in the form */}
      <FormField label={serviceMode ? t('calendar.fields.note') : t('calendar.fields.noteForDoctor')}>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder={t('calendar.fields.notePlaceholder')}
          rows={3}
          style={{ ...bookInputStyle, height: 'auto', padding: '10px 14px', resize: 'vertical', lineHeight: 1.7 }}
          onFocus={e => e.target.style.borderColor = C.navy}
          onBlur={e => e.target.style.borderColor = C.border}
        />
      </FormField>
    </>
  );

  const insuranceQuote = (
    <>
      {/* What the patient will actually pay, and who owes the rest — before
          they sit down. An insurer waiting on a معرفی‌نامه can be given one
          here, which is the whole reason to quote rather than discover it at
          the cash desk. */}
      {/* Only for a patient who already exists: a new one has no policies to
          quote against yet, and «ثبت سریع» has no patient at all. */}
      {!serviceMode && mode === 'existing' && selPatient?.id ? (
        <InsuranceQuotePanel
          patientId={selPatient.id}
          doctorId={isAuto && !engineMode ? null : (engineMode && engine.performer.type === 'STAFF' ? null : doctor?.id)}
          services={planned.map(({ catalogId, quantity }) => ({ catalogId, quantity }))}
          serviceDeclarationId={engineMode ? (engineRow?.declarationId || null) : serviceId}
          choice={insuranceChoice}
          onChoiceChange={setInsuranceChoice}
        />
      ) : null}

    </>
  );

  const form = (
    <>
      {notice}
      {queue ? (
        <div data-testid="booking-queue-mode" style={{ padding: '11px 14px', borderRadius: 12, background: C.bg, fontSize: '0.78125rem', lineHeight: 1.7, color: C.textSecondary }}>
          {t('reception.booking.queueMode')}
        </div>
      ) : (
      <TimeStep
        onHeldPick={engineMode ? (slot) => setOutsideAsk(slot) : null}
        onPlainPick={() => setAllowOutside(false)}
        allowFirstAvailable={allowFirstAvailable} slotMode={slotMode} setSlotMode={lookingAgain(setSlotMode)}
        modes={BOOKING_SLOT_MODES} onUseNext={takeNextSlot}
        fold={foldTime} optionsOpen={timeOptionsOpen} onToggleOptions={() => setTimeOptionsOpen(o => !o)}
        isAuto={isAuto} preview={preview} dayIsToday={dayIsToday} nextAvailable={nextAvailable}
        years={years} jYear={jYear} setJYear={lookingAgain(touching(setJYear))} jMonth={jMonth} setJMonth={lookingAgain(touching(setJMonth))}
        jDay={jDay} setJDay={lookingAgain(touching(setJDay))} days={days}
        availSlots={availSlots} slotsLoading={slotsLoading} noShift={noShift} absent={absent} allBooked={allBooked}
        offerUntimed={offerUntimed} untimed={untimed} untimedMode={untimedMode}
        isUntimed={isUntimed} selectedGDate={selectedGDate}
        untimedLeads={untimedLeads} compact={isMobile}
        selTime={selTime} setSelTime={touching(setSelTime)}
        slotsCollapsed={slotsCollapsed} onShowAllSlots={() => setGridClosed(false)}
        onSlotPicked={() => setGridClosed(true)}
      />
      )}
      {patientLocked ? (
        <FormField label={t('common.entity.patient')}>
          <div
            data-testid="booking-locked-patient"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              borderRadius: 12, border: `1.5px solid ${C.border}`, background: C.bg,
            }}
          >
            <div style={{
              width: 30, height: 30, borderRadius: '50%', background: C.navy, color: '#fff',
              fontSize: '0.6875rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {getInitials(lockedPatient.name || '')}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.84375rem', fontWeight: 700, color: C.textPrimary }}>{lockedPatient.name}</div>
              {lockedPatient.phone ? (
                <div dir="ltr" style={{ fontSize: '0.6875rem', color: C.textMuted, unicodeBidi: 'isolate', textAlign: 'end' }}>
                  {lockedPatient.phone}
                </div>
              ) : null}
            </div>
          </div>
        </FormField>
      ) : (
      <PatientStep
        mode={mode} switchMode={switchMode}
        searchQ={searchQ} setSearchQ={setSearchQ} searchRes={searchRes} searching={searching}
        selPatient={selPatient} setSelPatient={setSelPatient} linkedFrom={linkedFrom} setLinkedFrom={setLinkedFrom}
        newName={newName} setNewName={setNewName} newPhone={newPhone} setNewPhone={setNewPhone}
        phoneCandidates={candidatesPending ? phoneLookup.candidates : []}
        sharedPhoneCount={sharedPhoneOk ? phoneLookup.candidates.length : 0}
        onNewOnSameNumber={() => setSharedPhone(phoneLookup.number)}
        pickLinkedCandidate={pickLinkedCandidate} linkCandidate={linkCandidate}
        newInsurer={insurerChoice} setNewInsurer={setNewInsurer} region={region}
        phoneCountry={phoneCountry}
      />
      )}

      {/* «خدمت نوبت» — the service this appointment IS for, chosen once
          (docs/CLINIC_RECEPTION_SERVICE_INVOICE_DESIGN.md §S1). Pre-filled from
          the practitioner's default, so the common booking needs no touch.

          Hidden for «اولین نوبت across a profession», where no practitioner is
          chosen yet and so no price list applies — the BFF resolves the service
          once it knows who got the slot. */}
      {serviceMode ? (
        <FormField label={t('calendar.booking.service')}>
          <div
            data-testid="booking-locked-service"
            style={{
              padding: '10px 14px', borderRadius: 12, border: `1.5px solid ${C.border}`, background: C.bg,
            }}
          >
            <div style={{ fontSize: '0.84375rem', fontWeight: 700, color: C.textPrimary }}>{service.label}</div>
            {service.meta ? (
              <div style={{ fontSize: '0.71875rem', color: C.textMuted, marginTop: 2 }}>{service.meta}</div>
            ) : null}
            {service.notice ? (
              <div
                role="alert"
                data-testid="booking-service-notice"
                style={{
                  fontSize: '0.75rem', lineHeight: 1.7, color: CT.warnInk, marginTop: 6,
                  background: CT.amberLight, border: '1px solid rgba(245,158,11,0.30)',
                  borderRadius: 8, padding: '6px 10px',
                }}
              >
                {service.notice}
              </div>
            ) : null}
          </div>
        </FormField>
      ) : null}

      {engineMode ? (
        <FormField label={t('calendar.booking.service')}>
          {engineEntries.length === 1 ? (
            <div data-testid="booking-service-single" style={{ fontSize: '0.8125rem', color: C.textPrimary, padding: '7px 2px' }}>
              {engineEntryLabel(engineEntries[0])}
            </div>
          ) : (
            <select
              value={entry?.catalogId || ''}
              onChange={(e) => { setEntryId(e.target.value || null); setAllowOutside(false); }}
              style={bookInputStyle}
              data-testid="booking-service"
            >
              {engineEntries.map((e) => (
                <option key={e.catalogId} value={e.catalogId}>{engineEntryLabel(e)}</option>
              ))}
            </select>
          )}
        </FormField>
      ) : null}

      {!serviceMode && !editMode && !isAuto && !engineMode && doctor?.id ? (
        <FormField label={t('calendar.booking.service')}>
          {bookable.loading ? (
            <Skeleton height={34} />
          ) : bookable.services.length === 0 ? (
            /* Nothing priced this person's visit. Said as a fact with the way to
               fix it, and the booking is NOT blocked: a price is not a gate on
               giving somebody an appointment (§S2). */
            <div style={{
              fontSize: '0.75rem', lineHeight: 1.7, color: CT.warnInk,
              background: CT.amberLight, border: '1px solid rgba(245,158,11,0.30)',
              borderRadius: 8, padding: '8px 10px',
            }}>
              {t('calendar.booking.serviceNone')}
            </div>
          ) : bookable.services.length === 1 ? (
            /* One applicable service is a FACT, not a choice: rendering a select
               with a single option asks a question that has no second answer. */
            <div style={{ fontSize: '0.8125rem', color: C.textPrimary, padding: '7px 2px' }}>
              {serviceLabelOf(bookable.services[0], t, formatMoney)}
            </div>
          ) : (
            <select
              value={serviceId || ''}
              onChange={(e) => { setServiceTouched(true); setServiceId(e.target.value || null); }}
              style={bookInputStyle}
              data-testid="booking-service"
            >
              {bookable.services.map(sv => (
                <option key={sv.declarationId} value={sv.declarationId}>
                  {serviceLabelOf(sv, t, formatMoney)}
                </option>
              ))}
            </select>
          )}
          {chosenService && !(chosenService.unitPrice > 0) ? (
            <div style={{ fontSize: '0.71875rem', color: CT.warnInk, marginTop: 6 }}>
              {t('calendar.booking.serviceUnpriced')}
            </div>
          ) : null}
        </FormField>
      ) : null}

      {insuranceQuote}
      {deposit.shown ? (
        <BookingDepositBlock deposit={deposit} error={depositError} onTouch={() => setDepositError('')} />
      ) : null}
      <OptionsDisclosure
        // An invalid کد ملی blocks the submit, so it is never left folded out of sight.
        open={moreOpen || (newPatientExtras && nidInvalid)}
        onToggle={() => setMoreOpen(o => !o)}
        showLabel={t(newPatientExtras ? 'calendar.booking.moreOptionsNewPatient' : 'calendar.booking.moreOptions')}
        hideLabel={t('calendar.booking.lessOptions')}
        testId="booking-more-options">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {newPatientExtras ? (
            <NewPatientExtras
              newNid={newNid} setNewNid={setNewNid} nidInvalid={nidInvalid}
              newNidCountry={newNidCountry} setNewNidCountry={setNewNidCountry} nidCountry={nidCountry}
              newFileNumber={newFileNumber} setNewFileNumber={setNewFileNumber}
              fnBusy={fnBusy} suggestFileNumber={suggestFileNumber}
            />
          ) : null}
          {extrasPlanned}
          {extrasTail}
        </div>
      </OptionsDisclosure>

      {/* MARK: the shift admits this hour, the service does not — ask, then allow. */}
      {engineMode ? (
        <ConfirmDialog
          open={!!outsideAsk}
          onOpenChange={(o) => { if (!o) setOutsideAsk(null); }}
          title={t('reception.booking.outsideWindow')}
          description={t('reception.booking.outsideWindowConfirm', { service: entry?.name || '' })}
          confirmLabel={t('reception.booking.outsideWindowYes')}
          cancelLabel={t('common.actions.cancel')}
          data-testid="booking-outside-window"
          onConfirm={() => {
            setAllowOutside(true);
            setSelTime(outsideAsk.label);
            setGridClosed(true);
            setOutsideAsk(null);
          }}
        />
      ) : null}
    </>
  );

  // Carries no outer margin of its own: the desk insets it to the card's
  // gutter, the sheet's footer already has one.
  const heldNotice = heldByRequest ? (
    <div
      data-testid="slot-held-by-request"
      style={{
        padding: '9px 12px', borderRadius: 10, fontSize: '0.75rem', lineHeight: 1.7,
        background: C.amberLight, color: C.amber,
        border: `1px solid ${C.border}`,
      }}
    >
      {t('reception.requests.slotHeldWarning')}
    </div>
  ) : null;

  const actions = (
    <>
      <button
        onClick={onClose}
        style={{ flex: 1, height: 46, borderRadius: 12, border: `1.5px solid ${C.border}`, background: 'white', color: C.textSecondary, fontSize: '0.875rem', fontFamily: 'Meshkat, sans-serif', cursor: 'pointer', fontWeight: 600 }}>
        {t('common.actions.cancel')}
      </button>

      <button
        onClick={submit}
        disabled={!canSubmit || submitting}
        style={{
          flex: 2, height: 46, borderRadius: 12, border: 'none',
          background: canSubmit ? `linear-gradient(135deg, ${C.navyDark}, ${C.navy})` : C.border,
          color: canSubmit ? 'white' : C.textMuted,
          fontSize: '0.875rem', fontFamily: 'Meshkat, sans-serif',
          cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
          fontWeight: 700, transition: 'background 0.2s, color 0.2s',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
        }}>
        {submitting
          ? <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />{t('common.state.saving')}</>
          : <><svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>{submitLabel || (queue ? t('reception.booking.ticketSubmit') : isAuto ? t('calendar.actions.bookFirst') : t('reception.actions.book'))}</>}
      </button>
    </>
  );

  const title = editMode ? (editing.title || t('reception.edit.title')) : t('reception.actions.book');

  return (
    <AdaptiveModal
      onClose={onClose}
      title={title}
      // Booking is the longest task the phone carries — date, slot, patient,
      // insurance, notes — so it takes the whole viewport and docks its commit
      // row above the keyboard instead of letting it scroll away.
      snap={1}
      footer={(
        <div style={{ display: 'grid', gap: 10 }}>
          {heldNotice}
          <div style={{ display: 'flex', gap: 10 }}>{actions}</div>
        </div>
      )}
      desktop={(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 8000, background: 'rgba(15,28,46,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
          {keyframes}
          <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 540, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.26)', fontFamily: 'Meshkat, sans-serif', animation: 'modalIn 0.28s cubic-bezier(.22,1,.36,1) both', overflow: 'hidden' }}>

            {/* ── Header ── */}
            <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: '1.0625rem', fontWeight: 800, color: C.textPrimary }}>{title}</div>
                <button onClick={onClose} style={{ background: C.bg, border: 'none', cursor: 'pointer', color: C.textMuted, width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                </button>
              </div>
              {chips}
            </div>

            {/* ── Body ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              {form}
            </div>

            {/* ── Footer ── */}
            {heldNotice ? <div style={{ margin: '0 22px 12px' }}>{heldNotice}</div> : null}
            <div style={{ padding: '14px 22px', borderTop: `1px solid ${C.border}`, flexShrink: 0, display: 'flex', gap: 10 }}>
              {actions}
            </div>
          </div>
        </div>
      )}
    >
      {keyframes}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {chips}
        {form}
      </div>
    </AdaptiveModal>
  );
}

// ── Step 2: patient ────────────────────────────────────────────────────────────
function PatientStep(p) {
  const { t } = useTranslation();
  const { mode, switchMode } = p;
  return (
    <>
      <div>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.textSecondary, marginBottom: 10 }}>
          {t('common.entity.patient')} <span style={{ color: C.red }}>*</span>
        </div>

        {/* Mode switcher */}
        <div style={{ display: 'flex', border: `1.5px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
          {PATIENT_MODES(t).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => switchMode(key)}
              style={{ flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer', fontFamily: 'Meshkat, sans-serif', fontSize: '0.78125rem', fontWeight: 700, background: mode === key ? C.navy : 'white', color: mode === key ? 'white' : C.textSecondary, transition: 'background 0.18s, color 0.18s' }}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'existing' && <ExistingPatient {...p} />}
        {mode === 'new'      && <NewPatient {...p} />}
      </div>
    </>
  );
}

// What a hit is told apart by: موبایل, شماره پرونده, کد ملی (PM-0470). The search
// matches all three, so a hit on the file number has to SHOW the file number — a
// row of name + phone gives the desk no way to see why it came up.
function PatientIds({ pt }) {
  const { t, formatDigits } = useTranslation();
  const parts = [
    pt.fileNumber && t('reception.day.fileNumberChip', { number: formatDigits(pt.fileNumber) }),
    pt.nationalId && t('reception.search.nationalIdChip', { id: formatDigits(pt.nationalId) }),
  ].filter(Boolean);
  if (!pt.phone && !parts.length) return null;
  return (
    <div style={{ fontSize: '0.75rem', color: C.textMuted, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {pt.phone && <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{phoneLib.forDisplay(pt.phone)}</span>}
      {parts.map((x) => <span key={x}>{x}</span>)}
    </div>
  );
}

function ExistingPatient({ searchQ, setSearchQ, searchRes, searching, selPatient, setSelPatient, linkedFrom, setLinkedFrom }) {
  const { t } = useTranslation();
  // The field the desk starts on: opening ثبت نوبت means typing a name, a کد ملی or
  // a موبایل straight away. Disabled while a patient is chosen — that branch has no
  // input to focus, and the ref would sit on a node that never mounts.
  const searchRef = useAutoFocus(!selPatient);

  if (selPatient) {
    return (
      <>
        {linkedFrom && (
          <div style={{ marginBottom: 8, padding: '10px 13px', background: 'color-mix(in srgb, var(--p-navy) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--p-navy) 22%, transparent)', borderRadius: 10, fontSize: '0.75rem', color: C.navyDark, fontWeight: 600, lineHeight: 1.6 }}>
            {t('reception.booking.linkedFromPhoneNotice')}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'color-mix(in srgb, var(--p-navy) 8%, transparent)', borderRadius: 10, padding: '11px 14px' }}>
          <div>
            <div style={{ fontSize: '0.875rem', fontWeight: 700, color: C.textPrimary }}>{selPatient.name}</div>
            <PatientIds pt={selPatient} />
          </div>
          <button onClick={() => { setSelPatient(null); setLinkedFrom(null); }} style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 12px', fontSize: '0.75rem', color: C.textSecondary, cursor: 'pointer', fontFamily: 'Meshkat, sans-serif' }}>{t('operations.fields.change')}</button>
        </div>
      </>
    );
  }
  return (
    <div>
      <div style={{ position: 'relative' }}>
        <input
          ref={searchRef}
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          placeholder={t('calendar.search.placeholderIds')}
          aria-label={t('calendar.search.label')}
          style={bookInputStyle}
          onFocus={e => e.target.style.borderColor = C.navy}
          onBlur={e => e.target.style.borderColor = C.border}
        />
        <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: C.textMuted, display: 'flex' }}>
          {searching
            ? <div style={{ width: 14, height: 14, border: `2px solid ${C.border}`, borderTopColor: C.navy, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            : <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
          }
        </div>
      </div>
      {searchRes.length > 0 && (
        <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginTop: 6 }}>
          {searchRes.map((pt, i) => (
            <div
              key={pt.id}
              onClick={() => setSelPatient(pt)}
              style={{ padding: '11px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: i > 0 ? `1px solid ${C.border}` : 'none', transition: 'background 0.14s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--p-navy) 6%, transparent)'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: C.textPrimary }}>{pt.name}</div>
                <PatientIds pt={pt} />
              </div>
              <svg viewBox="0 0 24 24" fill={C.textMuted} width="14" height="14"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
            </div>
          ))}
        </div>
      )}
      {searchQ.length >= 2 && !searching && searchRes.length === 0 && (
        <div style={{ marginTop: 8, fontSize: '0.8125rem', color: C.textMuted, textAlign: 'center', padding: '10px 0' }}>
          {t('reception.booking.noneUseOtherTabs')}
        </div>
      )}
    </div>
  );
}

function NewPatient({
  newName, setNewName, newPhone, setNewPhone,
  newInsurer, setNewInsurer,
  phoneCandidates, sharedPhoneCount, onNewOnSameNumber,
  pickLinkedCandidate, linkCandidate, region = { iran: true, country: null },
  // The country the mobile starts on (PM-0218): the clinic country's, else its
  // region's. Absent — a caller that names none — is the region's, as before.
  phoneCountry = region.country,
}) {
  const { t, locale, formatDigits } = useTranslation();
  // Options come from the platform catalog, not from a list this client keeps —
  // «تامین اجتماعی» and «تأمین اجتماعی» were two different insurers to any query
  // for exactly as long as the label was the model.
  // Only بیمهٔ پایه: the caller writes this pick as the patient's ONE basic policy
  // (`kind: 'basic'`), so a تکمیلی here would be recorded as the wrong kind. A
  // supplementary is added on the patient's record. «آزاد» is a basic row and stays
  // — and abroad it is the only row (lib/insurance.js `registrationInsurers`).
  const { insurers: allInsurers } = useInsurers();
  const insurers = registrationInsurers(allInsurers.filter(isBasic), region);
  // «بیمار جدید» starts on the mobile, as «بیمار قبلی» starts on its search.
  const phoneRef = useAutoFocus();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* The kit field, not a raw `type="tel"`: this number is the patient's
          matching key against their own Keycloak account, and a desk-typed
          «۰۹۱۲…» that reached the server in Persian digits never matched it.
          `PhoneInput` self-labels, so no wrapping `FormField`. First, and where focus
          lands: the desk asks for the number before anything else, and the records
          already on it show up right here before a name is typed. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.tight }}>
        <PhoneInput
          // The kit reads `defaultCountry` once, at mount, and a clinic's country
          // may arrive after it — so the field starts over when it does. Iran is
          // still in the list abroad: a patient may well carry an Iranian number.
          key={phoneCountry || ''}
          ref={phoneRef}
          defaultCountry={phoneCountry || undefined}
          label={t('common.fields.mobile')}
          required
          value={newPhone}
          onChange={(e164) => setNewPhone(e164)}
          data-testid="booking-new-phone"
        />
        {phoneCandidates.length > 0 && (
          <>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.textSecondary }}>
              {t('reception.booking.phoneCandidatesHint')}
            </div>
            <PhoneCandidateList
              candidates={phoneCandidates}
              onChooseLinked={pickLinkedCandidate}
              chooseLabel={t('patients.phoneCandidates.choose')}
              onLink={linkCandidate}
              linkLabel={t('patients.phoneCandidates.addAndChoose')}
              onNewPatient={onNewOnSameNumber}
            />
          </>
        )}
        {sharedPhoneCount > 0 && (
          <div data-testid="shared-phone-note" style={{ fontSize: '0.71875rem', color: C.textMuted }}>
            {t('patients.form.sharedPhoneNote', { n: formatDigits(sharedPhoneCount) })}
          </div>
        )}
      </div>
      <FormField label={t('common.fields.fullName')} required>
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder={t('patients.form.fullNamePlaceholder')}
          style={bookInputStyle}
          onFocus={e => e.target.style.borderColor = C.navy}
          onBlur={e => e.target.style.borderColor = C.border}
        />
      </FormField>

      {/* Optional (PM-0125): the chosen chip again clears the choice. */}
      <FormField label={t('patients.fields.insurance')} helperText={t('common.form.optional')}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {insurers.map(ins => {
            const on = newInsurer === ins.id;
            return (
              <button
                key={ins.id}
                type="button"
                aria-pressed={on}
                onClick={() => setNewInsurer(on ? '' : ins.id)}
                style={{
                  padding: '7px 13px', borderRadius: 9, fontSize: '0.78125rem', fontWeight: 700,
                  fontFamily: 'Meshkat, sans-serif', cursor: 'pointer',
                  border: on ? `2px solid ${C.navy}` : `1.5px solid ${C.border}`,
                  background: on ? C.navy : 'white',
                  color: on ? 'white' : C.textPrimary,
                  transition: 'all 0.14s'
                }}>
                {insurerLabel(ins, locale)}
              </button>
            );
          })}
        </div>
      </FormField>

    </div>
  );
}

// «بیمار جدید»'s optional identity — کد ملی and شماره پرونده. Neither is needed to
// book, so they live in «گزینه‌های بیشتر» rather than between the desk and the submit.
function NewPatientExtras({
  newNid, setNewNid, nidInvalid, newNidCountry, setNewNidCountry, nidCountry = null,
  newFileNumber, setNewFileNumber, fnBusy, suggestFileNumber,
}) {
  const { t } = useTranslation();
  const fileNumberIssue = fileNumberProblem(newFileNumber);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* The kit's one national-id field (PM-0076) — was a raw box with its own
          checksum that dropped Persian digits (PM-0077). Starts on the clinic's
          country (PM-0061). */}
      <NationalIdInput
        label={t('common.fields.nationalId')}
        helperText={t('common.form.optional')}
        error={nidInvalid ? t('validation.nationalId.invalid') : undefined}
        value={newNid}
        onChange={e => setNewNid(e.target.value)}
        allowOtherCountries
        country={newNidCountry || undefined}
        defaultCountry={nidCountry || undefined}
        onCountryChange={setNewNidCountry}
        data-testid="booking-new-nid"
      />

      {/* شماره پرونده — this clinic's own number for the patient. Optional, and never
          filled in behind the desk's back: «تخصیص خودکار» asks the server for the
          clinic's next number and drops it in, still editable after. */}
      <FormField
        label={t('patients.fields.fileNumber')}
        error={fileNumberIssue ? t(`validation.fileNumber.${fileNumberIssue}`) : undefined}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <input
              value={newFileNumber}
              onChange={e => setNewFileNumber(e.target.value)}
              data-testid="booking-new-file-number"
              placeholder={t('patients.form.fileNumberHint')}
              dir="ltr"
              style={{ ...bookInputStyle, unicodeBidi: 'isolate', textAlign: 'start' }}
              onFocus={e => e.target.style.borderColor = C.navy}
              onBlur={e => e.target.style.borderColor = C.border}
            />
          </div>
          <button
            type="button"
            onClick={suggestFileNumber}
            disabled={fnBusy}
            title={t('patients.form.suggestNextFileNumber')}
            style={{
              height: 42, padding: '0 14px', borderRadius: 10, whiteSpace: 'nowrap',
              border: `1.5px solid ${C.border}`, background: '#fff',
              color: fnBusy ? C.textMuted : C.navy, fontSize: '0.78125rem', fontWeight: 700,
              fontFamily: 'Meshkat, sans-serif', cursor: fnBusy ? 'not-allowed' : 'pointer'
            }}>
            {fnBusy ? '...' : t('patients.form.autoAssign')}
          </button>
        </div>
      </FormField>

    </div>
  );
}

// ── Step 1: time ───────────────────────────────────────────────────────────────
function TimeStep({
  allowFirstAvailable, slotMode, setSlotMode, isAuto, preview, dayIsToday, nextAvailable,
  modes, onUseNext, fold, optionsOpen, onToggleOptions,
  years, jYear, setJYear, jMonth, setJMonth, jDay, setJDay, days,
  availSlots, slotsLoading, noShift, absent, allBooked, selTime, setSelTime,
  offerUntimed, untimed, untimedMode, isUntimed, selectedGDate, untimedLeads, compact,
  slotsCollapsed = false, onShowAllSlots, onSlotPicked,
  // The engine's MARK: an hour outside the service's window is drawn amber and,
  // pressed, asks (`onHeldPick`) instead of being taken; any other hour clears that.
  onHeldPick = null, onPlainPick = null,
}) {
  const { clinicMonthNames, locale } = useTranslation();
  const { t } = useTranslation();
  const { formatDigits } = useTranslation();
  const untimedIsNow = untimedMode === UNTIMED_NOW;
  // The day is kept as Jalali parts, and a Persian or Arabic desk picks it with the
  // three Jalali selects. An English, French or German desk reads Gregorian everywhere
  // else on the screen («Saturday, September 26, 2026» in the header), so it picks the
  // same day with the kit's date picker, which is Gregorian in those languages
  // (v0.19.0). The locale's OWN calendar decides, from its descriptor — never its id
  // (PM-0185). So does every desk of a brand on the Gregorian calendar: the kit's
  // picker follows the brand, and the three selects below are Jalali by construction.
  const gregorianPanel = LOCALES[locale]?.calendar === 'gregory' || calendarSystem('jalali') === 'gregory';
  const isoOf = (d) => dayIsoOf(d);
  const pickIso = (v) => {
    if (!v) return;
    const j = dateToJalali(dayDateOf(v));
    setJYear(j.y); setJMonth(j.m); setJDay(j.d);
  };
  // The questions that change WHEN: the mode and the day. Folded, because the
  // day-scoped default already answers them. «خارج از نوبت» is NOT in here — it
  // is asked before an hour is (PM-0056), so it stays one tap away.
  const controls = (
    <>
      {allowFirstAvailable && (
        <SlotModeSelector mode={slotMode} onChange={setSlotMode} dayIsToday={dayIsToday} modes={modes} />
      )}

      {/* «اولین نوبت پزشک» searches forward from now, so no date picker — and neither
          has «خارج از نوبت همین الان», which is today by definition. */}
      {slotMode !== 'firstAny' && !(isUntimed && untimedIsNow) && gregorianPanel && (
        <div style={{ marginBottom: 12 }}>
          <JalaliDatePicker value={isoOf(selectedGDate)} onChange={pickIso} />
        </div>
      )}
      {slotMode !== 'firstAny' && !(isUntimed && untimedIsNow) && !gregorianPanel && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div style={dropdownWrap}>
            <select value={jYear} onChange={e => setJYear(Number(e.target.value))} style={{ ...dropdownStyle, minWidth: 88 }}>
              {years.map(y => {
                return <option key={y} value={y}>{formatDigits(y)}</option>;
              })}
            </select>
            <ChevronDown />
          </div>
          <div style={{ ...dropdownWrap, flex: 1 }}>
            <select value={jMonth} onChange={e => setJMonth(Number(e.target.value))} style={{ ...dropdownStyle, width: '100%' }}>
              {clinicMonthNames().map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
            </select>
            <ChevronDown />
          </div>
          <div style={dropdownWrap}>
            <select value={jDay} onChange={e => setJDay(Number(e.target.value))} style={{ ...dropdownStyle, minWidth: 68 }}>
              {days.map(d => {
                return <option key={d} value={d}>{formatDigits(d)}</option>;
              })}
            </select>
            <ChevronDown />
          </div>
        </div>
      )}

    </>
  );
  const untimedRow = (separated) => (
    <UntimedRow
      untimed={untimed} mode={untimedMode} suggested={untimedLeads}
      separated={separated} compact={compact} selectedGDate={selectedGDate}
    />
  );
  // «همین الان» on leaves nothing to choose: no mode, no day, no hour.
  const showControls = !(isUntimed && untimedIsNow);
  const leads = offerUntimed && untimedLeads && !isUntimed;
  const timeOptions = !showControls ? null : fold ? (
    <OptionsDisclosure
      open={optionsOpen}
      onToggle={onToggleOptions}
      showLabel={t('doctors.slotModes.showOptions')}
      hideLabel={t('doctors.slotModes.hideOptions')}
      testId="booking-time-options" closedGap={12}>
      {controls}
    </OptionsDisclosure>
  ) : controls;
  return (
    <div style={{ background: C.bg, borderRadius: 14, padding: 14 }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.textSecondary, marginBottom: 10 }}>{t('calendar.fields.dateAndTime')}</div>

      {/* Where «خارج از نوبت» sits says how likely it is: leading when the doctor has
          nothing free here, following the suggestion («یا») otherwise. */}
      {leads && <div style={{ marginBottom: 12 }}>{untimedRow(false)}</div>}

      {/* Leading «همین الان»: the notice naming the next free slot comes before the
          options, as on the doctor's sheet. */}
      {!(leads && untimedIsNow) && timeOptions}

      {/* Nothing below this asks for an hour once «خارج از نوبت» is on — not the grid,
          not the «اولین نوبت» preview. A disabled grid would invite the desk to keep
          looking for a time that this booking does not have. */}
      {isUntimed ? null : isAuto ? (
        <FirstSlotPreview
          mode={slotMode} loading={preview.loading} error={preview.error} slot={preview.slot}
          dayIsToday={dayIsToday} next={nextAvailable}
          onUseNext={onUseNext}
        />
      ) : (
        <>
          <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: C.textMuted, marginBottom: 7 }}>{t('calendar.fields.appointmentTime')}</div>

          {slotsLoading ? (
            // The slot grid in placeholder: cells at a slot button's size, so the
            // free times land where the reader's eye already is.
            <SkeletonRegion
              label={t('calendar.slots.checking')}
              data-testid="booking-slots-skeleton"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {Array.from({ length: 12 }, (_, i) => <Skeleton key={i} width={58} height={32} radius={8} />)}
            </SkeletonRegion>
          ) : noShift ? (
            <div style={{ padding: '11px 14px', background: CT.amberLight, border: '1px solid rgba(245,158,11,0.30)', borderRadius: 10, fontSize: '0.78125rem', color: CT.warnInk, fontWeight: 600, lineHeight: 1.6 }}>
              {t('calendar.slots.noShift')}
            </div>
          ) : absent ? (
            <div style={{ padding: '11px 14px', background: CT.amberLight, border: '1px solid rgba(245,158,11,0.30)', borderRadius: 10, fontSize: '0.78125rem', color: CT.warnInk, fontWeight: 600, lineHeight: 1.6 }}>
              {t('calendar.slots.doctorAbsent')}
            </div>
          ) : allBooked ? (
            <div style={{ padding: '11px 14px', background: CT.redLight, border: '1px solid rgba(217,48,37,0.25)', borderRadius: 10, fontSize: '0.78125rem', color: C.red, fontWeight: 600, lineHeight: 1.6 }}>
              {t('calendar.slots.dayFull')}
            </div>
          ) : slotsCollapsed ? (
            // The chosen hour alone, drawn exactly as the grid draws a selected slot,
            // and the way back to every other free one.
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }} data-testid="booking-slot-chosen">
              <span
                aria-label={t('calendar.slots.chosenTime')}
                style={{
                  padding: '6px 11px', borderRadius: 8, fontSize: '0.8125rem', fontWeight: 600,
                  fontFamily: 'Meshkat, sans-serif', border: `2px solid ${C.navy}`,
                  background: C.navy, color: 'white',
                }}>
                {selTime}
              </span>
              {availSlots.length > 1 ? (
                <button
                  type="button"
                  onClick={onShowAllSlots}
                  data-testid="booking-slots-more"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8,
                    fontSize: '0.78125rem', fontWeight: 700, fontFamily: 'Meshkat, sans-serif', cursor: 'pointer',
                    border: `1.5px dashed ${C.border}`, background: 'white', color: C.navy,
                  }}>
                  {t('calendar.slots.otherTimes', { count: formatDigits(availSlots.length - 1) })}
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M7 10l5 5 5-5z" /></svg>
                </button>
              ) : null}
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {availSlots.map(slot => {
                const selected = selTime === slot.label;
                const held = !!slot.held && !selected;
                return (
                  <button
                    key={slot.label}
                    data-held={slot.held || undefined}
                    title={slot.held ? t('reception.booking.outsideWindow') : undefined}
                    onClick={() => {
                      if (slot.held && onHeldPick && !selected) { onHeldPick(slot); return; }
                      if (!slot.held) onPlainPick?.();
                      setSelTime(slot.label); onSlotPicked?.();
                    }}
                    style={{
                      padding: '6px 11px', borderRadius: 8, fontSize: '0.8125rem', fontWeight: 600,
                      fontFamily: 'Meshkat, sans-serif', cursor: 'pointer',
                      border: selected ? `2px solid ${C.navy}` : held ? `1.5px dashed ${CT.warn}` : `1.5px solid ${C.border}`,
                      background: selected ? C.navy : held ? CT.amberLight : 'white',
                      color: selected ? 'white' : held ? CT.warnInk : C.textPrimary,
                      transition: 'all 0.14s'
                    }}>
                    {slot.label}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {leads && untimedIsNow && <div style={{ marginTop: 12 }}>{timeOptions}</div>}
      {offerUntimed && !leads && <div style={{ marginTop: 12 }}>{untimedRow(true)}</div>}
    </div>
  );
}
